import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { TrackUpload } from "./TrackUpload";
import { Summary } from "./Summary";
import { TrackInfo } from "./TrackInfo";
import { useTracks } from "../hooks/useTracks";
import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { FormValues } from "./types";
import { useRelease } from "../hooks/useRelease";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import { SmartForm } from "ui-kit/SmartForm";
import { decorators } from "./utils";
import { ProgressSpinner } from "primereact/progressspinner";
import { useLanguages } from "../hooks/useLanguages";
import { bytesToHuman, isPerfDebugEnabled, perfGroup, perfGroupEnd, perfLog, perfNow } from "utils/perfDebug";


interface AudioFile {
  file: File
  url: string
  id: string
}
interface TrackUploadInfoProps {
  release: any,
  fetching: boolean
}

interface Language {
  id: number;
  language: string;
  code: string;
}

export const TrackUploadInfo: React.FC<TrackUploadInfoProps> = ({ release, fetching }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  const { id } = useParams();
  const { getAudio } = useTracks(id);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  // UI: по умолчанию поле пустое, "1" только как плейсхолдер.
  const [startTime, setStartTime] = useState<string>("")
  const [loading, setLoading] = useState(false);
  const [add, setAdd] = useState(false)
  const [editId, setEditId] = useState('new')
  const editIdRef = useRef<string>(editId);
  useEffect(() => {
    editIdRef.current = String(editId);
  }, [editId]);

  // When we create a track (Add to list), we want to auto-open that newly created track (usually the last one).
  const pendingSelectTrackIdRef = useRef<string | null>(null);
  const { addTrack, getTracks, tracks, updating, deleteTrack, reorderReleaseTracks, upDateRole } = useTracks();
  const { updateTrack } = useTracks(id);
  const { updateRelease, fetchRelease, release: releaseMeta } = useRelease(id)
  const { languages } = useLanguages() as { languages: Language[] };
  const [TurnOff, setTurnOff] = useState(false)
  const [caraoke, setCaraoke] = useState(false)
  const [audioChangedByUser, setAudioChangedByUser] = useState(false);
  const [replaceTrackId, setReplaceTrackId] = useState<string | null>(null);
  const [isFullScreenSaving, setIsFullScreenSaving] = useState(false);
  const [isListSaving, setIsListSaving] = useState(false);
  const [orderedTracks, setOrderedTracks] = useState<any[]>([]);
  // Keep initial full-screen loader until tracks list is fetched the first time.
  // IMPORTANT: in React 18 StrictMode (dev) effects can run twice; guard with a ref.
  const [isInitialTracksLoading, setIsInitialTracksLoading] = useState(true);
  const initialFetchStartedRef = useRef(false);
  const initialFetchDoneRef = useRef(false);
  const perfSessionRef = useRef<string>("");
  const perfT0Ref = useRef<number>(0);
  // Cache a couple of recently-used audio previews to avoid re-downloading when switching tracks.
  // Keep it tiny to avoid holding many huge WAVs in memory.
  const audioCacheRef = useRef<Map<string, { file: File; url: string }>>(new Map());
  const audioCacheOrderRef = useRef<string[]>([]);
  const prevAudioUrlsRef = useRef<string[]>([]);
  const audioAbortRef = useRef<AbortController | null>(null);
  const MAX_TRACKS = 12;

  // Ensure release metadata (title/cover) is available and fresh for the right sidebar on step 4/5.
  // IMPORTANT: prefer this hook's `releaseMeta` over the parent `release` prop,
  // because step 3 (ReleaseInfo) can update the release using a different hook instance,
  // leaving the top-level `release` prop stale until a full page reload.
  useEffect(() => {
    if (!id) return;
    fetchRelease({ backgroundRefresh: true }).catch(() => {
      // useRelease already sets `error`; avoid unhandled promise rejection noise
    });
  }, [id, fetchRelease]);
  useEffect(() => {
    if (!isPerfDebugEnabled()) return;
    if (!perfSessionRef.current) {
      perfSessionRef.current =
        (typeof crypto !== "undefined" && "randomUUID" in crypto)
          ? (crypto as any).randomUUID()
          : Math.random().toString(16).slice(2);
    }
    if (!perfT0Ref.current) perfT0Ref.current = perfNow();
    perfGroup("NewRelease TrackUploadInfo mount", {
      session: perfSessionRef.current,
      releaseId: id,
      step: new URLSearchParams(location.search).get("step"),
      href: typeof window !== "undefined" ? window.location.href : "",
    });
    perfLog("NewRelease.TrackUploadInfo.t0", { t0: perfT0Ref.current });
    perfGroupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke stale object URLs when audioFiles are replaced/cleared (prevents memory leaks during long sessions)
  useEffect(() => {
    const current = (audioFiles ?? []).map(x => x.url).filter(Boolean);
    const prev = prevAudioUrlsRef.current ?? [];
    const toRevoke = prev.filter(u => u && !current.includes(u));
    toRevoke.forEach(u => {
      try { URL.revokeObjectURL(u); } catch { /* noop */ }
    });
    prevAudioUrlsRef.current = current;
  }, [audioFiles]);

  const tracksCount = Array.isArray(orderedTracks) && orderedTracks.length
    ? orderedTracks.length
    : (Array.isArray(tracks) ? tracks.length : 0);
  const serverTracksCount = Array.isArray(tracks) ? tracks.length : 0;
  // Allow adding up to 12 tracks regardless of release type; only block while busy or limit reached.
  const disableAddTrack = loading || updating || tracksCount >= MAX_TRACKS;
  const isAddingNewTrack = add || String(editId) === "new";


  const formRef = useRef<HTMLFormElement>(null);
  // "Save track" visibility: track changes only after form auto-sync has settled
  // (language + genres normalization). This avoids false "Save track" when user didn't change anything.
  const saveBaselineRef = useRef<Record<string, string>>({});


  useEffect(() => {
    if (!Array.isArray(tracks)) return;

    // Stop the initial fullscreen loader only after the real fetch has started.
    // `tracks` may be `[]` initially (default state) before the request resolves.
    if (initialFetchStartedRef.current && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      setIsInitialTracksLoading(false);
    }

    // When user enters this step with 0 tracks, we want to immediately be in "draft/new track" mode:
    // - show a draft row in the right list
    // - show "Add track to list" as the primary action after user fills fields
    if (tracks.length === 0) {
      setOrderedTracks(prev => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some(t => String((t as any)?.id) === "new")) return list;
        return [{ id: "new", title: "New track", has_audio: false }];
      });
      setEditId("new");
      setAdd(true);
      return;
    }

    // When we do have tracks from server, ensure we exit draft mode.
    setAdd(false);
    const pending = pendingSelectTrackIdRef.current;
    if (pending && tracks.some((t: any) => String(t?.id) === String(pending))) {
      setEditId(String(pending));
      pendingSelectTrackIdRef.current = null;
    } else {
      const current = editIdRef.current;
      const hasCurrent =
        current && current !== "new" && tracks.some((t: any) => String(t?.id) === String(current));
      if (!hasCurrent) {
        const firstId = tracks?.[0]?.id;
        if (firstId) setEditId(String(firstId));
      }
    }
    setOrderedTracks(tracks.filter((t: any) => String(t?.id) !== "new"));
  }, [tracks]);

  // When switching tracks, reset baseline so "Save track" doesn't persist across selections.
  useEffect(() => {
    const key = String(editId);
    if (!key) return;
    delete saveBaselineRef.current[key];
  }, [editId]);
  
  useEffect(() => {
    // Fetch tracks once on mount; keep the big loader until we have the list.
    if (initialFetchStartedRef.current) return;
    initialFetchStartedRef.current = true;

    setIsInitialTracksLoading(true);
    (async () => {
      try {
        const perf = isPerfDebugEnabled();
        const t0 = perf ? perfNow() : 0;
        await getTracks(null, { backgroundRefresh: true });
        if (perf) {
          perfLog("NewRelease.TrackUploadInfo.initialTracksLoaded", {
            session: perfSessionRef.current,
            releaseId: id,
            ms: Math.round(perfNow() - t0),
          });
        }
      } catch (e) {
        // Don't spin forever on error.
        console.error("[TrackUploadInfo] initial getTracks failed", e);
        toast.error("Failed to load tracks");
        setIsInitialTracksLoading(false);
      }
    })();
  }, [getTracks]);


  const [tracksData, setTracksData] = useState<{
    id: string;
    title: string;
    has_audio: boolean;
    ai_generated?: boolean;
    explicit_content?: boolean;
    instrumental_music?: boolean;
    start_track_time?: string;
  }[]>([]);
  const queryTrackId = new URLSearchParams(location.search).get("trackId");
  const trackId = !add ? editId : "new";
  const isReplaceMode = replaceTrackId != null && String(replaceTrackId) === String(trackId);

  const EMPTY_TRACK = useMemo(
    () => ({
      id: "",
      title: "",
      has_audio: false,
      ai_generated: false,
      explicit_content: false,
      instrumental_music: false,
      start_track_time: "1", // backend default, UI всё равно управляется через startTime state
    }),
    []
  );

  // Selected track from the list. IMPORTANT: do NOT fallback to tracksData[0] when trackId is set,
  // otherwise navigation can briefly show/wire the wrong track and cause "flicker".
  const selectedTrack = useMemo(() => {
    if (!Array.isArray(tracksData)) return null;
    return tracksData.find((val: any) => String(val?.id) === String(trackId)) ?? null;
  }, [tracksData, trackId]);

  const track = trackId === "new" ? EMPTY_TRACK : (selectedTrack ?? EMPTY_TRACK);

  // Stabilize react-final-form initialValues to avoid reinitializing on background refreshes of tracks list.
  // We update initialValues only when the user switches tracks (trackId changes).
  const [stableInitialValues, setStableInitialValues] = useState<any>({});
  const stableInitialKeyRef = useRef<string>("");

  useEffect(() => {
    const key = String(trackId || "");
    if (!key) return;

    // Draft/new track
    if (key === "new") {
      if (stableInitialKeyRef.current !== key) {
        stableInitialKeyRef.current = key;
        setStableInitialValues({});
      }
      return;
    }

    // Wait until we actually have the selected track; don't "borrow" a different one.
    if (!selectedTrack || String(selectedTrack?.id) !== key) return;

    if (stableInitialKeyRef.current === key) return;
    stableInitialKeyRef.current = key;
    setStableInitialValues(selectedTrack);
  }, [trackId, selectedTrack]);
  const [formState, setFormState] = useState({
    ai_generated: false,
    explicit_content: false,
    instrumental_music: false
  });

  // Leaving a track should cancel replacement state
  useEffect(() => {
    if (!replaceTrackId) return;
    if (String(replaceTrackId) !== String(trackId)) {
      setReplaceTrackId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // On mobile, всегда открываем шаг 4–5 с самого верха,
    // даже если в URL остался hash от предыдущего шага.
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      const main = document.getElementById("main-scroll-container");
      if (main) {
        main.scrollTop = 0;
      }
      return;
    }

    // Desktop: сохраняем старое поведение с якорями
    const hash = location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash, isMobile]);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari fallback
    // eslint-disable-next-line deprecation/deprecation
    mql.addListener(onChange);
    // eslint-disable-next-line deprecation/deprecation
    return () => mql.removeListener(onChange);
  }, []);

  const loadServerAudioPreview = async () => {
    if (!trackId || trackId === "new") return;
    if (!track?.has_audio) return;

    // Cancel any in-flight audio load (user switched tracks / clicked again)
    if (audioAbortRef.current) {
      try { audioAbortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    audioAbortRef.current = controller;

    setLoading(true);
    try {
      const perf = isPerfDebugEnabled();
      const t0 = perf ? perfNow() : 0;
      if (perf) {
        perfGroup("NewRelease.TrackUploadInfo.getAudio", { session: perfSessionRef.current, trackId });
      }

      const cached = audioCacheRef.current.get(String(trackId));
      if (cached) {
        if (perf) {
          perfLog("NewRelease.TrackUploadInfo.getAudio.cacheHit", {
            session: perfSessionRef.current,
            trackId,
            bytes: bytesToHuman(cached.file?.size ?? 0),
          });
        }
        setAudioFiles([{ file: cached.file, url: cached.url, id: crypto.randomUUID() }]);
        setAudioChangedByUser(false);
        return;
      }

      const blob = await getAudio(String(trackId), { signal: controller.signal });
      const bytes = (blob as any)?.size ?? 0;
      const file = new File([blob], `track-${trackId}.wav`, { type: (blob as any)?.type || "audio/wav" });
      if (perf) {
        perfLog("NewRelease.TrackUploadInfo.getAudio.blob", {
          session: perfSessionRef.current,
          trackId,
          ms: Math.round(perfNow() - t0),
          blob: bytesToHuman(bytes),
          file: { name: file.name, size: bytesToHuman(file.size), type: file.type },
        });
      }

      const tUrl0 = perf ? perfNow() : 0;
      const url = URL.createObjectURL(file);
      if (perf) {
        perfLog("NewRelease.TrackUploadInfo.getAudio.createObjectURL", {
          session: perfSessionRef.current,
          trackId,
          ms: Math.round(perfNow() - tUrl0),
        });
      }

      // Maintain a tiny LRU cache (2 items) for quicker switching.
      audioCacheRef.current.set(String(trackId), { file, url });
      audioCacheOrderRef.current = [String(trackId), ...audioCacheOrderRef.current.filter(x => x !== String(trackId))];
      while (audioCacheOrderRef.current.length > 2) {
        const evictKey = audioCacheOrderRef.current.pop();
        if (!evictKey) break;
        const evicted = audioCacheRef.current.get(evictKey);
        audioCacheRef.current.delete(evictKey);
        if (evicted?.url) {
          try { URL.revokeObjectURL(evicted.url); } catch { /* noop */ }
        }
        if (perf) perfLog("NewRelease.TrackUploadInfo.getAudio.cacheEvict", { evictKey });
      }

      setAudioFiles([{ file, url, id: crypto.randomUUID() }]);
      // This preview came from server; do not re-upload it on save unless user changes audio.
      setAudioChangedByUser(false);
    } catch (error: any) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        // user switched tracks / canceled; ignore
      } else {
        console.error("[TrackUploadInfo] loadServerAudioPreview failed", error);
        setAudioFiles([]);
      }
    } finally {
      if (isPerfDebugEnabled()) perfGroupEnd();
      setLoading(false);
    }
  };

  // When switching tracks, do NOT auto-download audio. If we already cached a preview, show it instantly.
  useEffect(() => {
    // Cancel any in-flight request for previous track.
    if (audioAbortRef.current) {
      try { audioAbortRef.current.abort(); } catch { /* noop */ }
      audioAbortRef.current = null;
    }
    setLoading(false);

    if (!trackId || trackId === "new") {
      setAudioFiles([]);
      setAudioChangedByUser(false);
      return;
    }

    const cached = audioCacheRef.current.get(String(trackId));
    if (cached) {
      setAudioFiles([{ file: cached.file, url: cached.url, id: crypto.randomUUID() }]);
      setAudioChangedByUser(false);
      return;
    }
    // No cached preview -> keep empty until user clicks "Load preview"
    setAudioFiles([]);
    setAudioChangedByUser(false);
  }, [trackId]);


  // Normalize backend track data into the shape expected by the Track form (especially contributors).
  // IMPORTANT: backend now returns *per-role* contributor rows:
  // [{ id, contributor: { id, name, roles[], instruments[] }, role, instrument }, ...]
  // Для формы нам нужен ОДИН объект на контрибутора, а роли/инструменты — это роли/инструменты
  // именно в ЭТОМ треке, а не все глобальные роли контрибутора.
  const normalizeTrackContributors = (list: any): any[] => {
    if (!Array.isArray(list)) return [];
    const byId = new Map<string, { id: string; name: string; title: string; roles: any[]; instruments: any[] }>();

    list.filter(Boolean).forEach((item: any) => {
      const base = item?.contributor ?? item;
      const rawRoles =
        // В первую очередь берём трековые роли (role / roles на самом item),
        // а не глобальные роли контрибутора.
        (Array.isArray(item?.roles) && item.roles.length ? item.roles :
          item?.role ? [item.role] :
            Array.isArray(base?.roles) && base.roles.length ? base.roles : []);
      const rawInstruments =
        // Только трековые инструменты (instrument / instruments) с release_track_contributors.
        // Глобальные инструменты контрибутора (base.instruments) здесь игнорируем,
        // чтобы не «подставлять» их автоматически во всех треках.
        (Array.isArray(item?.instruments) && item.instruments.length
          ? item.instruments
          : item?.instrument
            ? [item.instrument]
            : []);

      const idRaw = base?.id ?? item?.id;
      if (idRaw == null) return;
      const key = String(idRaw);
      const name = base?.name ?? base?.title ?? "";

      if (!byId.has(key)) {
        byId.set(key, {
          id: key,
          name,
          title: name,
          roles: [],
          instruments: [],
        });
      }
      const entry = byId.get(key)!;

      const pushUnique = (arr: any[], val: any, getKey: (v: any) => string) => {
        if (!val) return;
        const k = getKey(val);
        if (!k) return;
        if (arr.some((x) => getKey(x) === k)) return;
        arr.push(val);
      };

      rawRoles.forEach((r: any) => pushUnique(entry.roles, r, (r0) => String(r0?.id ?? r0?.name ?? "")));
      rawInstruments.forEach((inst: any) => pushUnique(entry.instruments, inst, (i0) => String(i0?.id ?? i0?.name ?? "")));
    });

    return Array.from(byId.values());
  };

  useEffect(() => {
    if (!tracks) return;
    const normalized = tracks.map((t: any) => ({
      ...t,
      contributors: normalizeTrackContributors(t?.contributors),
    }));
    setTracksData(normalized);
  }, [tracks]);

  // When switching tracks, sync checkbox state + playback start from the selected track once.
  useEffect(() => {
    if (!trackId || trackId === "new") return;
    if (!selectedTrack) return;

    const newFormState = {
      ai_generated: selectedTrack.ai_generated ?? false,
      explicit_content: selectedTrack.explicit_content ?? false,
      instrumental_music: selectedTrack.instrumental_music ?? false,
    };

    setFormState((prev) => (JSON.stringify(prev) === JSON.stringify(newFormState) ? prev : newFormState));

    const nextStart = Math.max(1, Number.parseInt(String(selectedTrack.start_track_time ?? "1"), 10) || 1);
    setStartTime((prev) => (prev === String(nextStart) ? prev : String(nextStart)));
  }, [trackId, selectedTrack]);

  // Когда переходим в режим "draft/new track" (trackId === "new"),
  // нужно сбрасывать чекбоксы More и Start of playback в дефолт,
  // чтобы значения с предыдущего трека не подтягивались.
  useEffect(() => {
    if (trackId !== "new") return;
    setFormState({
      ai_generated: false,
      explicit_content: false,
      instrumental_music: false,
    });
    setStartTime("");
  }, [trackId]);

  // Keep editId in sync with URL (?trackId=...) so refresh / deep-links work.
  useEffect(() => {
    if (!queryTrackId) return;
    setAdd(false);
    setEditId(String(queryTrackId));
  }, [queryTrackId]);

  // If no trackId provided, default to the first existing track (when not adding a new one)
  useEffect(() => {
    if (add) return;
    if (editId && editId !== "new") return;
    const firstId = tracksData?.[0]?.id;
    if (firstId) setEditId(String(firstId));
  }, [add, editId, tracksData]);


  const resolveMetadataLanguageId = (value?: string) => {
    if (!value) return "";
    const found = languages?.find(
      lang => String(lang.code) === String(value) || String(lang.id) === String(value)
    );
    return found ? String(found.id) : String(value);
  };

  const normalizeStartTrackTime = (value: any) => {
    const n = Number.parseInt(String(value ?? "").trim(), 10);
    // UI should never send 0; minimum is 1 second.
    return Number.isFinite(n) ? Math.max(1, n) : 1;
  };

  // For the right sidebar (Summary) we want the freshest release metadata available.
  // `releaseMeta` comes from this hook's own fetch (just above), while `release` is
  // passed from the parent NewRelease container and can be outdated right after step 3 submit.
  const summaryRelease: any = releaseMeta ?? release;

  const snapshotTrack = (v: any, playbackStart: any) => {
    // Keep snapshot stable & minimal to drive "Save track" visibility
    const normalizeIds = (arr: any) =>
      Array.isArray(arr) ? arr.map((x: any) => (typeof x === "object" ? x?.id : x)).map(String).sort() : [];

    const normalizeArtist = (a: any) => ({
      id: String(a?.id ?? ""),
      role: a?.role ?? a?.pivot?.role ?? undefined,
    });

    const normalizeContributor = (c: any) => ({
      id: String(c?.id ?? ""),
      roles: Array.isArray(c?.roles) ? c.roles.map((r: any) => (typeof r === "string" ? r : r?.name)).filter(Boolean).sort() : [],
      // Храним все роли/инструменты, но без дублей по id/названию
      instruments: Array.isArray(c?.instruments)
        ? c.instruments
            .map((i: any) => (typeof i === "string" ? { name: i } : i))
            .filter((it: any, idx: number, arr: any[]) => {
              const key = String(it?.id ?? it?.name ?? "");
              if (!key) return false;
              return arr.findIndex((x: any) => String(x?.id ?? x?.name ?? "") === key) === idx;
            })
            .map((i: any) => (i?.id ?? i?.name))
        : [],
    });

    const mainGenre = Array.isArray(v?.main_genre)
      ? (v.main_genre[0] != null ? String(v.main_genre[0]) : "")
      : (v?.main_genre != null ? String(v.main_genre) : "");

    return JSON.stringify({
      title: v?.title ?? "",
      version: v?.version ?? "",
      metadata_language: v?.metadata_language ?? "",
      recording_year: v?.recording_year ?? "",
      lyrics: v?.lyrics ?? "",
      isrc: v?.isrc ?? "",
      // IMPORTANT: these toggles are controlled via local `formState` (TrackUploadContent),
      // so they must be part of dirty detection here.
      ai_generated: !!formState?.ai_generated,
      explicit_content: !!formState?.explicit_content,
      instrumental_music: !!formState?.instrumental_music,
      start_track_time: normalizeStartTrackTime(playbackStart),
      main_genre: mainGenre,
      genre_ids: normalizeIds(v?.genre_ids),
      // Для целей dirty-check достаточно знать, какие именно роли/инструменты
      // привязаны к артистам/контрибьюторам. Убираем дубли, чтобы список не рос
      // при дублирующихся ролях/инструментах от бэка.
      artists: Array.isArray(v?.artists)
        ? v.artists
            .map(normalizeArtist)
            .filter((a: any) => a.id)
            .map((a: any) => ({
              id: a.id,
              role: a.role,
            }))
            .sort((a: any, b: any) => a.id.localeCompare(b.id))
        : [],
      contributors: Array.isArray(v?.contributors)
        ? v.contributors
            .map(normalizeContributor)
            .filter((c: any) => c.id)
            .map((c: any) => ({
              id: c.id,
              roles: c.roles,
              instruments: c.instruments,
            }))
            .sort((a: any, b: any) => a.id.localeCompare(b.id))
        : [],
    });
  };

  // NOTE: baseline snapshot is established from form `values` when switching tracks (see render below),
  // because `track` may not match post-normalized form values (languages/genres), causing false "dirty".

  const onSubmit = async (values: FormValues) => {
    console.log(values.metadata_language, 'values.metadata_language');

    if (process.env.NODE_ENV !== "production") {
      console.log("[TrackUploadInfo.onSubmit] saving track draft (update)", {
        releaseId: id,
        trackId,
        hasAudioFile: !!audioFiles?.[0]?.file,
        audio: audioFiles?.[0]?.file instanceof File
          ? { name: audioFiles[0].file.name, size: audioFiles[0].file.size, type: audioFiles[0].file.type }
          : null,
      });
    }

    setTurnOff(true);

    const cleanNumberArray = (arr?: (number | string)[]) =>
      Array.from(
        new Set(
          (arr ?? [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n))
        )
      );

    const pickIds = <T extends { id?: number | string }>(arr?: T[]) =>
      (arr ?? []).map(x => x.id).filter(id => id != null).map(String);

    const genreIds = cleanNumberArray(values.genre_ids);
    const artistIds = pickIds(values.artists);
    const contributors = values.contributors ?? [];
    const artists = Array.isArray(values.artists) ? values.artists : [];

    const syncTrackArtistRoles = async (savedTrackId: string | number) => {
      const tId = Number(savedTrackId);
      if (!Number.isFinite(tId) || !artists.length) return;

      const primary =
        artists.find((a: any) => String(a?.role ?? a?.pivot?.role ?? "").toUpperCase() === "MAIN") ??
        artists.find((a: any) => a?.checked === true) ??
        artists[0];

      const primaryId = Number(primary?.id);
      if (!Number.isFinite(primaryId)) return;

      const others = artists
        .map((a: any) => Number(a?.id))
        .filter((aId: number) => Number.isFinite(aId) && aId !== primaryId);

      // Persist roles on server (backend defaults may reset MAIN to first artist otherwise)
      await Promise.all([
        upDateRole(tId, primaryId, "MAIN", { refresh: false }),
        ...others.map((aId: number) => upDateRole(tId, aId, "FEAT", { refresh: false })),
      ]);
    };

    const form = new FormData();

    form.append("title", values.title ?? "");
    genreIds.forEach(id => form.append("genre_ids[]", String(id)));

    const mainGenreValue = Array.isArray(values.main_genre)
      ? values.main_genre[0] ?? ""
      : values.main_genre ?? "";
    form.append("main_genre", String(mainGenreValue));

    form.append("version", values.version ?? "");
    form.append("language_id", resolveMetadataLanguageId(values.metadata_language ?? "en"));

    form.append("metadata_language", resolveMetadataLanguageId(values.metadata_language ?? "en"));
    if (values.isrc) form.append("isrc", values.isrc);

    form.append("recording_year", String(values.recording_year ?? new Date().getFullYear()));

    if (!caraoke) {
      form.append("lyrics", values.lyrics ?? "");
    }


    form.append("ai_generated", formState.ai_generated ? "1" : "0");
    form.append("explicit_content", formState.explicit_content ? "1" : "0");
    form.append("instrumental_music", formState.instrumental_music ? "1" : "0");
    form.append("start_track_time", String(normalizeStartTrackTime(startTime)));


    if (!trackId || trackId === "new") {
      form.append("track_id", "");
    }

    const isReplacingExistingTrack =
      !!isReplaceMode &&
      !!trackId &&
      trackId !== "new" &&
      audioFiles?.[0]?.file instanceof File;

    // Only upload audio if it's a new track OR user explicitly changed audio for an existing track.
    if (audioFiles?.[0]?.file instanceof File && (trackId === "new" || !trackId || audioChangedByUser)) {
      form.append("audio", audioFiles[0].file, audioFiles[0].file.name);
    }

    artistIds.forEach(id => form.append("artist_ids[]", id));

    // release_track_contributors payload for backend:
    // For each contributor and for EACH role we create a separate row:
    // [{ contributor_id, role_id, instrument_id, share, is_primary }, ...]
    const releaseTrackContributors = (() => {
      const rows: {
        contributor_id: number;
        role_id: number;
        instrument_id: number | null;
      }[] = [];

      (contributors || []).forEach((c: any) => {
        const contributorId = Number((c as any)?.id);
        if (!Number.isFinite(contributorId)) return;

        const rawRoles =
          (Array.isArray((c as any)?.roles) && (c as any).roles.length
            ? (c as any).roles
            : (c as any)?.role
              ? [(c as any).role]
              : []);
        const rawInstruments =
          (Array.isArray((c as any)?.instruments) && (c as any).instruments.length
            ? (c as any).instruments
            : (c as any)?.instrument
              ? [(c as any).instrument]
              : []);

        const primaryInstrument =
          rawInstruments.length && rawInstruments[0]?.id != null
            ? Number(rawInstruments[0].id)
            : null;

        rawRoles.forEach((r: any) => {
          const roleId = Number(r?.id);
          if (!Number.isFinite(roleId)) return;
          rows.push({
            contributor_id: contributorId,
            role_id: roleId,
            instrument_id: primaryInstrument,
          });
        });
      });

      return rows;
    })();

    releaseTrackContributors.forEach((row, index) => {
      form.append(`release_track_contributors[${index}][contributor_id]`, String(row.contributor_id));
      form.append(`release_track_contributors[${index}][role_id]`, String(row.role_id));
      if (row.instrument_id != null && Number.isFinite(row.instrument_id)) {
        form.append(`release_track_contributors[${index}][instrument_id]`, String(row.instrument_id));
      }
    });

    if (process.env.NODE_ENV !== "production") {
      console.log("[TrackUploadInfo.onSubmit] payload prepared", {
        releaseId: id,
        trackId,
        hasAudioFile: !!audioFiles?.[0]?.file,
        releaseTrackContributors,
      });
    }

    try {
      if (trackId && trackId !== "new" && isReplacingExistingTrack) {
        // Replace flow:
        // - do NOT change server state until user hits "Save track"
        // - on save: update THIS track with new audio (no delete)
        if (!audioChangedByUser) {
          // User didn't actually pick a new file; keep old track unchanged.
          return;
        }

        if (process.env.NODE_ENV !== "production") {
          console.log("[TrackUploadInfo.onSubmit] replace track audio -> updateTrack", { trackId });
        }

        const updated = await updateTrack(String(trackId), form);
        await syncTrackArtistRoles(String(trackId));
        await getTracks(updated);

        setReplaceTrackId(null);
        setAudioFiles([]);
        setAudioChangedByUser(false);
        return updated;

      } else if (trackId && trackId !== "new") {
        if (process.env.NODE_ENV !== "production") {
          console.log("[TrackUploadInfo.onSubmit] updateTrack ->", { trackId });
        }
        const updated = await updateTrack(String(trackId), form);
        if (process.env.NODE_ENV !== "production") {
          console.log("[TrackUploadInfo.onSubmit] updateTrack OK", { trackId });
        }
        // Ensure primary artist role persists after save
        await syncTrackArtistRoles(String(trackId));
        if (process.env.NODE_ENV !== "production") console.time("[TrackUploadInfo] refresh tracks after update");
        await getTracks();
        if (process.env.NODE_ENV !== "production") console.timeEnd("[TrackUploadInfo] refresh tracks after update");

        // IMPORTANT:
        // Do NOT overwrite Release Information (step 3) with Track Information (step 5),
        // even for SINGLE. Review must reflect the release-level fields from step 3.
        return updated;
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.log("[TrackUploadInfo.onSubmit] addTrack ->", { releaseId: id });
        }
        const created: any = await addTrack(String(id), form);
        if (!created?.id) {
          throw new Error("Track create failed: empty response");
        }
        pendingSelectTrackIdRef.current = String(created.id);
        if (process.env.NODE_ENV !== "production") {
          console.log("[TrackUploadInfo.onSubmit] addTrack OK", { releaseId: id, trackId: created.id });
        }
        // Ensure primary artist role persists after creation
        await syncTrackArtistRoles(created.id);
        if (process.env.NODE_ENV !== "production") console.time("[TrackUploadInfo] refresh tracks after create");
        await getTracks(created);
        if (process.env.NODE_ENV !== "production") console.timeEnd("[TrackUploadInfo] refresh tracks after create");
        // Replace local draft row in right list (id="new") with created track (if present)
        setOrderedTracks((prev: any[]) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((t: any) => (String(t?.id) === "new" ? created : t));
        });
        if (created?.id) {
          setEditId(String(created.id));
        }
        setAdd(false);
        setAudioFiles([]);
        // Новый трек -> пустое поле, "1" только как плейсхолдер
        setStartTime("");
      setAudioChangedByUser(false);
        return created;
      }
    } catch (err) {
      setTurnOff(false);
      console.error("[TrackUploadInfo.onSubmit] save failed", err);

      const error = err as AxiosError<{
        errors?: Record<string, { code: string; message: string }[]>;
      }>;

      const backendErrors = error.response?.data?.errors;
      if (process.env.NODE_ENV !== "production") {
        console.log("[TrackUploadInfo.onSubmit] backend validation errors", {
          status: error.response?.status,
          errors: backendErrors,
        });
      }
      let message = "Something went wrong";
      let key = "";

      if (backendErrors) {
        const firstField = Object.keys(backendErrors)[0];
        if (firstField && backendErrors[firstField]?.[0]?.message) {
          message = backendErrors[firstField][0].message;
          key = backendErrors[firstField][0].code ?? "";
        }
      } else if (error.message) {
        message = error.message;
      }

      toast.error(
        <FormattedMessage
          id={`NewRelease.ReleaseInfo.${key}`}
          defaultMessage={message}
        />
      );
      throw err;
    } finally {
      setTurnOff(false);
    }
  };

  const onAddTrack = async (values: FormValues,) => {

    setTurnOff(true)
    if (process.env.NODE_ENV !== "production") {
      console.log("[TrackUploadInfo.onAddTrack] add track button -> creating new track", {
        releaseId: id,
        hasAudioFile: !!audioFiles?.[0]?.file,
        audio: audioFiles?.[0]?.file instanceof File
          ? { name: audioFiles[0].file.name, size: audioFiles[0].file.size, type: audioFiles[0].file.type }
          : null,
      });
    }
    const cleanNumberArray = (arr?: (number | string)[]) =>
      Array.from(
        new Set(
          (arr ?? [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n))
        )
      );


    const pickIds = <T extends { id?: number | string }>(arr?: T[]) =>
      (arr ?? []).map(x => x.id).filter(id => id != null).map(String);

    const genreIds = cleanNumberArray(values.genre_ids);
    const artistIds = pickIds(values.artists);
    const contributorsList = values.contributors ?? [];

    const form = new FormData();
    form.append("title", values.title ?? "");
    genreIds.forEach(id => form.append("genre_ids[]", String(id)));
    form.append("main_genre", String(values.main_genre ?? ""));
    form.append("version", values.version ?? "");
    form.append("metadata_language", resolveMetadataLanguageId(values.metadata_language ?? "en"));
    if (values.isrc) {
      form.append("isrc", values.isrc ?? "");
    }
    form.append("recording_year", String(values.recording_year ?? new Date().getFullYear()));
    form.append("lyrics", values.lyrics ?? "");
    form.append("ai_generated", formState.ai_generated ? "1" : "0");
    form.append("explicit_content", formState.explicit_content ? "1" : "0");
    form.append("instrumental_music", formState.instrumental_music ? "1" : "0");
    form.append("start_track_time", String(normalizeStartTrackTime(startTime)));

    if (!trackId || trackId === "new") {
      form.append("track_id", "");
    }

    // Add-to-list always creates a new track -> must include audio
    if (audioFiles?.[0]?.file instanceof File) {
      form.append("audio", audioFiles[0].file, audioFiles[0].file.name);
    }
    artistIds.forEach(id => form.append("artist_ids[]", id));

    // Same release_track_contributors payload for Add-to-list flow
    const addReleaseTrackContributors = (() => {
      const rows: {
        contributor_id: number;
        role_id: number;
        instrument_id: number | null;
      }[] = [];

      (contributorsList || []).forEach((c: any) => {
        const contributorId = Number((c as any)?.id);
        if (!Number.isFinite(contributorId)) return;

        const rawRoles =
          (Array.isArray((c as any)?.roles) && (c as any).roles.length
            ? (c as any).roles
            : (c as any)?.role
              ? [(c as any).role]
              : []);
        const rawInstruments =
          (Array.isArray((c as any)?.instruments) && (c as any).instruments.length
            ? (c as any).instruments
            : (c as any)?.instrument
              ? [(c as any).instrument]
              : []);

        const primaryInstrument =
          rawInstruments.length && rawInstruments[0]?.id != null
            ? Number(rawInstruments[0].id)
            : null;

        rawRoles.forEach((r: any) => {
          const roleId = Number(r?.id);
          if (!Number.isFinite(roleId)) return;
          rows.push({
            contributor_id: contributorId,
            role_id: roleId,
            instrument_id: primaryInstrument,
          });
        });
      });

      return rows;
    })();

    addReleaseTrackContributors.forEach((row, index) => {
      form.append(`release_track_contributors[${index}][contributor_id]`, String(row.contributor_id));
      form.append(`release_track_contributors[${index}][role_id]`, String(row.role_id));
      if (row.instrument_id != null && Number.isFinite(row.instrument_id)) {
        form.append(`release_track_contributors[${index}][instrument_id]`, String(row.instrument_id));
      }
    });

    if (process.env.NODE_ENV !== "production") {
      console.log("[TrackUploadInfo.onAddTrack] payload prepared", {
        releaseId: id,
        hasAudioFile: !!audioFiles?.[0]?.file,
        contributorsRaw: contributorsList,
        releaseTrackContributors: addReleaseTrackContributors,
      });
    }

    try {
      const created: any = await addTrack(String(id), form)
      if (!created?.id) {
        throw new Error("Track create failed: empty response");
      }
      pendingSelectTrackIdRef.current = String(created.id);
      if (process.env.NODE_ENV !== "production") {
        console.log("[TrackUploadInfo.onAddTrack] addTrack OK", { releaseId: id, trackId: created.id });
      }
      if (process.env.NODE_ENV !== "production") console.time("[TrackUploadInfo] refresh tracks after add-to-list");
      await getTracks(created);
      if (process.env.NODE_ENV !== "production") console.timeEnd("[TrackUploadInfo] refresh tracks after add-to-list");

      if (process.env.NODE_ENV !== "production") console.time("[TrackUploadInfo] fetchRelease after add-to-list");
      await fetchRelease()
      if (process.env.NODE_ENV !== "production") console.timeEnd("[TrackUploadInfo] fetchRelease after add-to-list");
      if (created?.id) setEditId(String(created.id));
      setAdd(false);
      setAudioFiles([]);
      // После Add to list начинаем следующий трек с пустого поля
      setStartTime("");
      setAudioChangedByUser(false);
    } catch (err) {
      setTurnOff(false)
      console.error("[TrackUploadInfo.onAddTrack] addTrack failed", err);
      const error = err as AxiosError<{
        errors?: Record<string, { code: string; message: string }[]>;
      }>;

      const backendErrors = error.response?.data?.errors;
      if (process.env.NODE_ENV !== "production") {
        console.log("[TrackUploadInfo.onAddTrack] backend validation errors", {
          status: error.response?.status,
          errors: backendErrors,
        });
      }
      let message = "Something went wrong";
      let key = "";

      if (backendErrors) {
        const firstField = Object.keys(backendErrors)[0];
        if (firstField && backendErrors[firstField]?.[0]?.message) {
          message = backendErrors[firstField][0].message;
          key = backendErrors[firstField][0].code ?? "";
        }
      } else if (error.message) {
        message = error.message;
      }

      toast.error(
        <FormattedMessage
          id={`NewRelease.ReleaseInfo.${key}`}
          defaultMessage={message}
        />
      );
      throw err;
    } finally {
      setTurnOff(false);
    }
  };

  if (updating) {
    // For saving tracks we show scoped loaders (list-only / button), not fullscreen.
    // Fullscreen is reserved for Save+Next navigation only.
  }

  return (
    <SmartForm<FormValues>
      onSubmit={onSubmit}
      decorators={[decorators]}
      validate={(values) => {

        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.title || String(values.title).trim().length === 0) {
          errors.title = "Track title is required.";
        }

        // Track version is optional

        if (!values.metadata_language || String(values.metadata_language).trim().length === 0) {
          errors.metadata_language = "Track language is required.";
        }

        // recording_year is stored as number-like value
        const ry = Number(values.recording_year as any);
        if (!Number.isFinite(ry) || ry <= 0) {
          errors.recording_year = "Recording year is required.";
        }

        // Artists list must be non-empty
        if (!Array.isArray(values.artists) || values.artists.filter((a: any) => a?.id).length === 0) {
          errors.artists = "At least one artist is required.";
        }

        // Contributors list must be non-empty
        if (!Array.isArray(values.contributors) || values.contributors.filter((c: any) => c?.id).length === 0) {
          errors.contributors = "At least one contributor is required.";
        }

        // Primary genre required (stored as number[] with selectionLimit=1)
        const mainGenre = Array.isArray(values.main_genre)
          ? values.main_genre[0]
          : (values.main_genre as any);
        if (mainGenre == null || String(mainGenre).trim() === "") {
          errors.main_genre = "Primary genre is required.";
        }

        if (!caraoke) {
          if (!values.lyrics || values.lyrics.trim().length === 0) {
            errors.lyrics = "Lyrics field is required.";
          } else if (values.lyrics.trim().length < 3) {
            errors.lyrics = "Lyrics must be at least 10 characters long.";
          }
        }
        return errors;
      }}
      initialValues={stableInitialValues}

      ref={formRef}
    >
      {({ values, hasValidationErrors, form }: any) => {
        const currentKey = String(editId);
        const currentSnapshot = currentKey !== "new" ? snapshotTrack(values, startTime) : null;

        // Wait until auto-sync effects have stabilized:
        // Also: ensure the form is actually initialized for the selected track id.
        const trackLoaded = currentKey !== "new" && String(values?.id ?? "") === String(currentKey);

        // - language is converted to a valid language code (so the select can display it)
        // - genres array (backend) has been mirrored into main_genre + genre_ids (form fields)
        const languageSettled =
          currentKey === "new" ||
          (!!values?.metadata_language &&
            Array.isArray(languages) &&
            languages.length > 0 &&
            languages.some((l: any) => String(l?.code) === String(values?.metadata_language)));

        const computeExpectedGenres = () => {
          if (!Array.isArray(values?.genres)) return null;
          const primary = values.genres.find((g: any) => g?.is_primary)?.id;
          const additional = values.genres
            .filter((g: any) => !g?.is_primary)
            .map((g: any) => g?.id);
          const expectedMain = primary != null ? String(Number(primary)) : "";
          const expectedAdditional = Array.from(
            new Set((additional ?? []).map((x: any) => String(Number(x))).filter((x: any) => x !== "NaN"))
          ).sort();
          return { expectedMain, expectedAdditional };
        };

        const expectedGenres = computeExpectedGenres();
        const currentMain = Array.isArray(values?.main_genre)
          ? (values.main_genre?.[0] != null ? String(values.main_genre[0]) : "")
          : (values?.main_genre != null ? String(values.main_genre) : "");
        const currentAdditional = Array.isArray(values?.genre_ids)
          ? values.genre_ids.map((x: any) => String(x)).sort()
          : [];
        const genresSettled =
          !expectedGenres ||
          (currentMain === expectedGenres.expectedMain &&
            JSON.stringify(currentAdditional) === JSON.stringify(expectedGenres.expectedAdditional));

        const artistsSettled = (() => {
          if (currentKey === "new") return true;
          if (!Array.isArray(values?.artists) || values.artists.length === 0) return false;
          const roles = values.artists.map((a: any) => String(a?.role ?? a?.pivot?.role ?? "")).map((r: string) => r.toUpperCase());
          const mainCount = roles.filter((r: string) => r === "MAIN").length;
          return mainCount === 1;
        })();

        const dirtyEnabledForThisTrack =
          currentKey !== "new" && trackLoaded && languageSettled && genresSettled && artistsSettled;

        // Capture baseline exactly when dirty tracking becomes enabled (after auto-sync settles).
        if (dirtyEnabledForThisTrack && currentSnapshot && !saveBaselineRef.current[currentKey]) {
          saveBaselineRef.current[currentKey] = currentSnapshot;
        }

        const baseline = dirtyEnabledForThisTrack ? saveBaselineRef.current[currentKey] : null;
        const isTrackDirty =
          dirtyEnabledForThisTrack && !!baseline && !!currentSnapshot
            ? currentSnapshot !== baseline
            : false;

        return (

        <Container $isMobile={isMobile}>
          {(isFullScreenSaving || isInitialTracksLoading) && (
            <Loading>
              <ProgressSpinner />
            </Loading>
          )}
          <StepsContainer>
            <StepContainer>
              <TrackUpload
                audioFiles={audioFiles}
                setAudioFiles={(action) => {
                  setAudioChangedByUser(true);
                  setAudioFiles(action as any);
                }}
                serverAudioAvailable={!!track?.has_audio && String(trackId) !== "new"}
                onLoadServerAudio={loadServerAudioPreview}
                replaceMode={!!isReplaceMode}
                onStartReplace={() => {
                  if (!trackId || String(trackId) === "new") return;
                  setReplaceTrackId(String(trackId));
                  // show upload UI and let user pick a new file, but do NOT delete old until Save track.
                }}
                onCancelReplace={() => {
                  setReplaceTrackId(null);
                  setAudioFiles([]);
                  setAudioChangedByUser(false);
                }}
                startTime={startTime}
                setStartTime={setStartTime}
                setFormState={setFormState}
                formState={formState}
                loading={loading}
                saving={isListSaving}
              />
            </StepContainer>
            
            
            <StepContainer>
              <TrackInfo
                editId={editId}
                audioFiles={audioFiles}
                startTime={startTime}
                track={track}
                formState={formState}
                release={release}
                id={""}
                fetching={fetching}
                onSubmit={onSubmit}
                TurnOff={TurnOff}
                hasValidationErrors={hasValidationErrors}
                values={values}
                caraoke={caraoke}
                setCaraoke={setCaraoke}
                tracks={tracks}
                savedTracksCount={serverTracksCount}
                isAddingNewTrack={isAddingNewTrack}
                hasAudioFile={!!audioFiles?.[0]?.file}
                forceSaveTrack={!!isReplaceMode && !!audioChangedByUser && !!audioFiles?.[0]?.file}
                isDirty={isTrackDirty}
                saving={isListSaving}
                onCopyFromPrevious={() => {
                  const list = Array.isArray(orderedTracks) ? orderedTracks.filter(Boolean) : [];
                  // Draft row is id="new". For copying we always want a *saved* previous track.
                  const savedList = list.filter((t: any) => String(t?.id) !== "new");
                  if (!savedList.length) return;

                  const currentId = String(editId);
                  const currentIsDraft = currentId === "new";

                  // If current is draft -> copy from the last saved track (e.g. 3rd -> into 4th draft).
                  // If current is an existing track -> copy from the previous saved track in order.
                  const savedIdx = savedList.findIndex((t: any) => String(t?.id) === currentId);
                  const prev =
                    currentIsDraft
                      ? savedList[savedList.length - 1]
                      : savedIdx > 0
                        ? savedList[savedIdx - 1]
                        : savedList[0];
                  if (!prev) return;

                  if (process.env.NODE_ENV !== "production") {
                    console.log("[TrackUploadInfo] Copy from previous", {
                      releaseId: id,
                      fromTrackId: prev?.id,
                      to: currentId,
                    });
                  }

                  const normalizeArtists = (artists: any) => {
                    if (!Array.isArray(artists)) return [];
                    const getRole = (a: any) =>
                      a?.role ?? a?.pivot?.role ?? a?.track_role ?? a?.trackRole ?? undefined;

                    // Determine the real primary artist id from previous track.
                    // Priority: explicit role MAIN -> is_primary -> checked/status (legacy) -> first artist.
                    const primaryId =
                      artists.find((a: any) => String(getRole(a)).toUpperCase() === "MAIN")?.id ??
                      artists.find((a: any) => a?.is_primary === true || a?.is_primary === 1 || a?.is_primary === "1")?.id ??
                      artists.find((a: any) => a?.checked === true || a?.status === true)?.id ??
                      artists[0]?.id ??
                      null;

                    return artists
                      .filter(Boolean)
                      .map((a: any) => ({
                        id: String(a?.id ?? ""),
                        name: a?.name ?? a?.title ?? "",
                        // Avoid duplicated display (UI shows both name + title)
                        title:
                          a?.title && a.title !== a?.name
                            ? a.title
                            : "",
                        // IMPORTANT: preserve the actual primary artist.
                        role: primaryId != null && String(a?.id) === String(primaryId) ? "MAIN" : "FEAT",
                        checked: primaryId != null && String(a?.id) === String(primaryId),
                        // Do NOT default `status` to true (it may mean "active", not "primary").
                        status: a?.status ?? false,
                      }))
                      .filter((a: any) => a.id);
                  };

                  const normalizeContributors = (contributors: any) => {
                    // Reuse the same normalization that we use when loading tracks from backend,
                    // so it correctly handles both:
                    // - flat shape: { id, name, roles[], instruments[] }
                    // - nested shape: { id, contributor: { id, name, roles[], instruments[] }, role, instrument }
                    return normalizeTrackContributors(contributors);
                  };

                  const normalizeMainGenre = (v: any) => {
                    // Form expects number[] with selectionLimit=1
                    if (Array.isArray(v)) {
                      const first = v[0];
                      const n = Number(first);
                      return Number.isFinite(n) ? [n] : [];
                    }
                    const n = Number(v);
                    return Number.isFinite(n) ? [n] : [];
                  };

                  const normalizeAdditionalGenreIds = (v: any) => {
                    // Form expects (number|string)[] of ids
                    if (!v) return [];
                    if (Array.isArray(v)) {
                      return v
                        .map((x: any) => (typeof x === "object" ? x?.id : x))
                        .map((x: any) => Number(x))
                        .filter((n: any) => Number.isFinite(n));
                    }
                    return [];
                  };

                  const genresFromPrev = Array.isArray(prev.genres) ? prev.genres : null;
                  const primaryFromGenres = genresFromPrev?.find((g: any) => g?.is_primary)?.id;
                  const additionalFromGenres = genresFromPrev
                    ? genresFromPrev.filter((g: any) => !g?.is_primary).map((g: any) => g?.id)
                    : [];

                  const fieldsToCopy: Array<[string, any]> = [
                    ["title", prev.title],
                    ["version", prev.version],
                    ["metadata_language", prev.metadata_language],
                    ["recording_year", prev.recording_year],
                    ["lyrics", prev.lyrics],
                    ["isrc", prev.isrc],
                    ["ai_generated", prev.ai_generated],
                    ["explicit_content", prev.explicit_content],
                    ["instrumental_music", prev.instrumental_music],
                    ["main_genre", primaryFromGenres != null ? [Number(primaryFromGenres)] : normalizeMainGenre(prev.main_genre)],
                    ["genre_ids", additionalFromGenres.length ? normalizeAdditionalGenreIds(additionalFromGenres) : normalizeAdditionalGenreIds(prev.genre_ids)],
                    // keep genres array in sync too (prevents later effects from overwriting selection)
                    ["genres", genresFromPrev ?? prev.genres],
                    // IMPORTANT: copy artists & contributors too (requested), but never copy audio.
                    ["artists", normalizeArtists(prev.artists)],
                    ["contributors", normalizeContributors(prev.contributors)],
                  ];

                  fieldsToCopy.forEach(([k, v]) => {
                    if (v !== undefined) form.change(k, v);
                  });
                  // Do NOT copy Start of playback (requested). Audio/playback start stays as-is.
                }}
                onSaveToList={async () => {
                  if (hasValidationErrors) return;
                  setIsListSaving(true);
                  try {
                    await onSubmit(values);
                  } finally {
                    setIsListSaving(false);
                  }
                }}
                onSaveCurrent={async () => {
                  if (hasValidationErrors) return;
                  setIsListSaving(true);
                  try {
                    await onSubmit(values);
                    // Mark current values as saved so "Save track" hides immediately
                    if (currentKey && currentKey !== "new") {
                      saveBaselineRef.current[currentKey] = snapshotTrack(values, startTime);
                    }
                  } finally {
                    setIsListSaving(false);
                  }
                }}
                onSaveAndNext={async () => {
                  if (hasValidationErrors) return;
                  setIsFullScreenSaving(true);
                  try {
                    await onSubmit(values);
                    navigate(`/releases/new-release/${id}?step=publish`);
                  } finally {
                    setIsFullScreenSaving(false);
                  }
                }}
                bottomContent={isMobile ? (
                  <MobileSummaryWrapper>
                    <Summary
                      orderedTracks={orderedTracks}
                      setOrderedTracks={setOrderedTracks}
                      onSubmit={onSubmit}
                      release={summaryRelease}
                      editId={editId}
                      setEditId={setEditId}
                      setAudioFiles={setAudioFiles}
                      coverUrl={summaryRelease?.cover_url}
                      tracksData={tracksData}
                      setTracksData={setTracksData}
                      setLoading={setLoading}
                      setAdd={setAdd}
                      add={add}
                      values={values}
                      onAddTrack={onAddTrack}
                      disableAddTrack={disableAddTrack}
                      listLoading={isListSaving}
                      deleteTrack={deleteTrack}
                      refreshTracks={() => getTracks()}
                      reorderReleaseTracks={reorderReleaseTracks}
                    />
                  </MobileSummaryWrapper>
                ) : null}
              />
            </StepContainer>
          </StepsContainer>
          {!isMobile && (
            <Summary
              orderedTracks={orderedTracks}
              setOrderedTracks={setOrderedTracks}
              onSubmit={onSubmit}
              release={summaryRelease}
              editId={editId}
              setEditId={setEditId}
              setAudioFiles={setAudioFiles}
              coverUrl={summaryRelease?.cover_url}
              tracksData={tracksData}
              setTracksData={setTracksData}
              setLoading={setLoading}
              setAdd={setAdd}
              add={add}
              values={values}
              onAddTrack={onAddTrack}
              disableAddTrack={disableAddTrack}
              listLoading={isListSaving}
              deleteTrack={deleteTrack}
              refreshTracks={() => getTracks()}
              reorderReleaseTracks={reorderReleaseTracks}
            />
          )}
        </Container>
        );
      }}
    </SmartForm>
  );
};

const Container = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  gap: 18px;
  background-color: ${props => props.theme.palette.background.base};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 18px;
    overflow-x: hidden;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-radius: 30px;
  background-color: ${props => props.theme.palette.background.primary};

  @media (max-width: 768px) {
    flex: unset;
    width: 100%;
  }
`;

const MobileSummaryWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;